/**
 * useUserCalendarIntegrations
 *
 * Hook para buscar integrações de calendário do usuário autenticado
 * Usa endpoint /api/user-integrations/my com filtro de capability
 */

import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

export interface CalendarIntegration {
  id: string;
  providerId: string;
  providerName: string;
  status: 'active' | 'pending' | 'error' | 'inactive' | 'auth_failed' | 'disconnected' | 'expired';
  credentials?: {
    accessToken: string;
    refreshToken?: string;
    expiresAt: string;
  };
  authFailureReason?: string;
  authFailedAt?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Hook para buscar user-integrations de calendário do usuário
 *
 * @returns Query com lista de integrações de calendário
 *
 * @example
 * const { data: calendars = [], isLoading } = useUserCalendarIntegrations();
 */
export const useUserCalendarIntegrations = () => {
  return useQuery({
    queryKey: ['user-calendar-integrations'],
    queryFn: async () => {
      const response = await api.get<{ data: CalendarIntegration[] }>('/user-integrations/my', {
        params: {
          capability: 'calendar_sync'  // Filtra apenas calendários
        }
      });
      // Endpoint retorna array direto em data, não objeto com items
      return response.data.data;
    },
    staleTime: 5 * 60 * 1000,  // 5 minutos
    retry: 2
  });
};
